import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	createApprovalUnit,
	deleteApprovalUnit,
	getApprovalUnits,
	getFilterApprovalUsers,
	updateApprovalUnit,
} from '@api/manageApprovalUnit';

import { extractQueryParams } from '@utils/common';
import { usersApprovalFormatted } from '@utils/formatter';
import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TOpenCloseProps } from '@interfaces/common/handlers';
import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	IApprovalUnitDTO,
	ICreateApprovalUnitDTO,
	IUpdateApprovalUnitDTO,
} from '@interfaces/organisation/dto/approvalUnitsDTO';
import { IApprovalUser } from '@components/routes/AdminPanel/ManageApprovalUnits/interfaces';

import { ESnackbarTypes } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import { refetchOptions } from '@constants/common';
import {
	APPROVAL_UNITS,
	AVAILABLE_APPROVAL_UNITS,
	CREATE_APPROVAL_UNIT,
	DELETE_APPROVAL_UNIT,
	FILTER_APPROVAL_USERS,
	UPDATE_APPROVAL_UNIT,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export const useApprovalUnits = (params?: IApprovalUnitDTO & Partial<TOpenCloseProps>) => {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		pageSize,
		page,
		sortFieldName,
		sortDirection,
		searchValue,
		userId,
		onClose,
		onOpen,
	} = params || {};

	const {
		data,
		isLoading: isLoadingApprovalUnit,
		isFetching: isFetchingApprovalUnit,
	} = useQuery({
		queryKey: [
			APPROVAL_UNITS,
			{
				page,
				pageSize,
				sortDirection,
				sortFieldName,
				searchValue,
				userId,
			},
		],
		queryFn: extractQueryParams(getApprovalUnits),
		...refetchOptions,
	});

	const { data: filterApprovalUsers } = useQuery({
		queryKey: [FILTER_APPROVAL_USERS],
		queryFn: extractQueryParams(getFilterApprovalUsers),
		select: (options: IApprovalUser[]) => {
			return usersApprovalFormatted(options);
		},
		enabled: [!!page],
		...refetchOptions,
	});

	const { mutate: handleCreateApprovalUnit } = useMutation<ICreateApprovalUnitDTO>({
		mutationKey: [CREATE_APPROVAL_UNIT],
		mutationFn: createApprovalUnit,
		onSuccess: () => {
			if (onClose) onClose();

			dispatch(setSnackbar({
				message: responseMessages.createApprovalUnit,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([APPROVAL_UNITS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([AVAILABLE_APPROVAL_UNITS]),
			];
		},
	});

	const { mutate: handleUpdateApprovalUnit } = useMutation<IUpdateApprovalUnitDTO>({
		mutationKey: [UPDATE_APPROVAL_UNIT],
		mutationFn: updateApprovalUnit,
		onSuccess: () => {
			if (onClose) onClose();

			dispatch(setSnackbar({
				message: responseMessages.updateApprovalUnit,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([APPROVAL_UNITS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([AVAILABLE_APPROVAL_UNITS]),
			];
		},
	});

	const { mutate: handleDeleteApprovalUnit } = useMutation<{ unitId: string }>({
		mutationKey: [DELETE_APPROVAL_UNIT],
		mutationFn: deleteApprovalUnit,
		onSuccess: () => {
			if (onClose) onClose();

			dispatch(setSnackbar({
				message: responseMessages.removeApprovalUnit,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([APPROVAL_UNITS]),
				queryClient.invalidateQueries([FILTER_APPROVAL_USERS]),
				queryClient.invalidateQueries([AVAILABLE_APPROVAL_UNITS]),
			];
		},
		onError: (err: IResponseError) => {
			const isAssigned = err.errors.find((item) => item.code === EErrorCodes.approvalUnitHaveAttach);

			if (onOpen && !!isAssigned && onOpen) onOpen();
			else handleErrorFromMessage(err);
		},
	});

	return {
		approvalUnitData: data?.items || [],
		countOfRecords: data?.count || 0,
		filterApprovalUsers: filterApprovalUsers || [],
		isLoadingApprovalUnit: isLoadingApprovalUnit || isFetchingApprovalUnit,
		handleCreateApprovalUnit,
		handleUpdateApprovalUnit,
		handleDeleteApprovalUnit,
	};
};
